import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TInventory } from "~/types/inventory";
import { InventoryStatusCode } from "~/utils/constants";
import { capitalize, formatDimensions } from "~/utils";

export type ExportType = "csv" | "pdf" | "photo-list";

interface ExportData {
  [key: string]: string | number | undefined;
}

// Helper function to get client name
const getClientName = (client: TInventory["client"]) => {
  if (client.companyName) {
    return client.companyName;
  }
  const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
  return fullName || "Unknown";
};

// Helper function to render title with sub-client name if applicable
const renderTitle = (item: TInventory) => {
  const baseTitle = item.title || "";
  if (item.client?.isSubClient) {
    const subClientName = getClientName(item.client);
    return `${baseTitle}${baseTitle ? " " : ""}--${subClientName}--`;
  }
  return baseTitle;
};

// Format status text
const formatStatusText = (status?: InventoryStatusCode) => {
  const statusText = capitalize(status || "");
  if (statusText === "No Possession") {
    return "No AHC Possession";
  }
  return statusText;
};

// Get cell value for a column
const getCellValue = (item: TInventory, columnKey: string): string => {
  switch (columnKey) {
    case "inventoryRef":
      return item.inventoryRef || "-";
    case "clientInternalRef":
      return item.clientInternalRef || "-";
    case "title":
      return renderTitle(item);
    case "artistName":
      return item.artistName || "-";
    case "medium":
      return item.medium || "-";
    case "dimensions":
      return formatDimensions(item);
    case "packingType":
      return item.packingType
        ? capitalize(item.packingType.replace(/_/g, " "))
        : "-";
    case "status":
      return formatStatusText(item.status);
    case "location":
      return item.status === InventoryStatusCode.AVAILABLE_ON_HAND
        ? item.location?.name || "In Storage"
        : "-";
    case "value":
      return item.value ? `$${item.value.toLocaleString()}` : "-";
    case "weight":
      return item.weight ? `${item.weight} lbs` : "-";
    case "description":
      return item.description || "-";
    case "notes":
      return item.notes || "-";
    case "pieceCount":
      return item.pieceCount || "-";
    case "packageCount":
      return item.packageCount || "-";
    default:
      return "-";
  }
};

// Get column header label
const getColumnLabel = (columnKey: string): string => {
  const labels: { [key: string]: string } = {
    inventoryRef: "Ref #",
    clientInternalRef: "Client Ref #",
    title: "Title",
    artistName: "Artist",
    medium: "Medium",
    dimensions: "Dimensions",
    packingType: "Packing Type",
    status: "Status",
    location: "Location",
    value: "Value",
    weight: "Weight",
    description: "Description",
    notes: "Notes",
    pieceCount: "Piece Count",
    packageCount: "Package Count",
  };
  return labels[columnKey] || columnKey;
};

// Escape HTML special characters
const escapeHtml = (text: string): string => {
  const map: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

// Export to CSV
export const exportToCSV = (
  inventories: TInventory[],
  columns: string[],
  action: "view" | "download"
) => {
  // Create header row
  const headers = columns.map((col) => getColumnLabel(col));
  const headerRow = headers.join(",");

  // Create data rows
  const dataRows = inventories.map((item) => {
    return columns
      .map((col) => {
        const value = getCellValue(item, col);
        // Escape commas and quotes in CSV
        if (value.includes(",") || value.includes('"') || value.includes("\n")) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
      .join(",");
  });

  // Combine header and data
  const csvContent = [headerRow, ...dataRows].join("\n");

  // Create blob
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  if (action === "view") {
    // Open in new tab
    const newWindow = window.open(url, "_blank");
    if (!newWindow) {
      // Fallback to download if popup blocked
      const link = document.createElement("a");
      link.href = url;
      link.download = `inventory-export-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    // Download
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventory-export-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Clean up URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Export to PDF
export const exportToPDF = (
  inventories: TInventory[],
  columns: string[],
  action: "view" | "download"
) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Set title
  doc.setFontSize(16);
  doc.text("Inventory Export", 14, 15);

  // Prepare table data
  const headers = columns.map((col) => getColumnLabel(col));
  const rows = inventories.map((item) =>
    columns.map((col) => getCellValue(item, col))
  );

  // Add table
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 20,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 20 },
  });

  // Generate PDF blob
  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);

  if (action === "view") {
    // Open in new tab
    const newWindow = window.open(url, "_blank");
    if (!newWindow) {
      // Fallback to download if popup blocked
      const link = document.createElement("a");
      link.href = url;
      link.download = `inventory-export-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    // Download
    doc.save(`inventory-export-${Date.now()}.pdf`);
  }

  // Clean up URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Export Photo List (HTML format with images)
export const exportPhotoList = (
  inventories: TInventory[],
  columns: string[],
  action: "view" | "download"
) => {
  // Create HTML content
  const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Inventory Photo List</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 20px;
      background-color: #fffef0;
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
      border-bottom: 2px solid black;
      padding-bottom: 20px;
    }
    .header h1 {
      margin: 0;
      font-size: 24px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 2px;
    }
    .item {
      page-break-inside: avoid;
      margin-bottom: 30px;
      border: 2px solid black;
      padding: 20px;
      background-color: white;
    }
    .item-header {
      display: flex;
      justify-content: space-between;
      align-items: start;
      margin-bottom: 15px;
      border-bottom: 1px solid #ccc;
      padding-bottom: 10px;
    }
    .item-title {
      font-size: 18px;
      font-weight: bold;
      flex: 1;
    }
    .item-ref {
      font-size: 14px;
      color: #666;
      margin-left: 20px;
    }
    .item-content {
      display: grid;
      grid-template-columns: 200px 1fr;
      gap: 20px;
    }
    .item-image {
      width: 200px;
      height: 200px;
      object-fit: cover;
      border: 1px solid #ccc;
      background-color: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #999;
    }
    .item-details {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
    .detail-item {
      margin-bottom: 8px;
    }
    .detail-label {
      font-weight: bold;
      font-size: 12px;
      text-transform: uppercase;
      color: #666;
      margin-bottom: 2px;
    }
    .detail-value {
      font-size: 14px;
    }
    @media print {
      body {
        margin: 0;
      }
      .item {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Inventory Photo List</h1>
    <p>Total Items: ${inventories.length}</p>
  </div>
  ${inventories
    .map((item) => {
      const imageUrl =
        item.images && item.images.length > 0
          ? item.images[0].url
          : null;
      const noImageElement = `<div class="item-image" style="display: ${imageUrl ? "none" : "flex"};">No Image</div>`;

      const details = columns
        .filter((col) => col !== "title" && col !== "inventoryRef")
        .map((col) => {
          const value = getCellValue(item, col);
          if (value === "-") return "";
          const escapedValue = escapeHtml(value);
          const escapedLabel = escapeHtml(getColumnLabel(col));
          return `
            <div class="detail-item">
              <div class="detail-label">${escapedLabel}</div>
              <div class="detail-value">${escapedValue}</div>
            </div>
          `;
        })
        .filter((d) => d)
        .join("");

      const escapedTitle = escapeHtml(renderTitle(item));
      const escapedRef = escapeHtml(item.inventoryRef || "N/A");
      const escapedImageAlt = escapeHtml(item.title || "No image");

      return `
        <div class="item">
          <div class="item-header">
            <div class="item-title">${escapedTitle}</div>
            <div class="item-ref">Ref: ${escapedRef}</div>
          </div>
          <div class="item-content">
            <div>
              ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapedImageAlt}" class="item-image" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` : ""}
              ${noImageElement}
            </div>
            <div class="item-details">
              ${details}
            </div>
          </div>
        </div>
      `;
    })
    .join("")}
</body>
</html>
  `;

  // Create blob
  const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  if (action === "view") {
    // Open in new tab
    const newWindow = window.open(url, "_blank");
    if (!newWindow) {
      // Fallback to download if popup blocked
      const link = document.createElement("a");
      link.href = url;
      link.download = `inventory-photo-list-${Date.now()}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    // Download
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventory-photo-list-${Date.now()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Clean up URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

